import { Component, EventEmitter, inject, Inject, Output } from '@angular/core';
import {
  CONFIDENTIALITIES,
  Tag,
  AngularComponentInputs,
  LabelOption,
  People,
  DEFAULT_GOOGLE_AVATAR_URI,
  Property,
  FilePropertyOption,
  FilePropertyPeople,
  PropertyOption,
  UserFromSearchDto,
} from 'app/types';
import { DialogState } from 'app/pages/side-panel/side-panel.types';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { PeopleService } from 'app/services/people/people.service';
import { map } from 'rxjs';

@Component({
  selector: 'app-properties-dialog',
  templateUrl: './properties-dialog.component.html',
  styleUrl: './properties-dialog.component.scss',
})
export class PropertiesDialogComponent {
  @Output() closeDialog = new EventEmitter();

  private peopleService = inject(PeopleService);

  readonly newTagId: number = -1;
  readonly confidentialityOptions = CONFIDENTIALITIES;
  readonly DialogState = DialogState;
  readonly default_avatar_uri: string = DEFAULT_GOOGLE_AVATAR_URI;

  cancelPropertiesDialog: Function = () => {};
  savePropertiesDialog: Function = () => {};

  tagOptions: Tag[] = [];
  searchedTags: Tag[] = [];
  tagsInput: Tag[] = [];
  confidentialitiesInput?: string = undefined;
  documentTypeInput?: string = undefined;
  languageInput?: string = undefined;
  customLabels: Property[] = [];
  selectedCustomLabelsOptions: LabelOption[][] = [];
  selectedCustomLabelsPeople: People[][] = [];
  peopleLabelFieldAssociatedArray: (number | undefined)[] = [];
  hasRight: boolean = false;
  usersFromSearch: UserFromSearchDto[] = [];

  constructor(
    @Inject('INPUTS') inputs: AngularComponentInputs,
    @Inject('OUTPUTS') public outputs: AngularComponentInputs,
  ) {
    this.tagOptions = inputs.tags;
    this.tagsInput = inputs.fileTags;
    this.confidentialitiesInput = this.upperCaseFirstLetter(
      inputs.fileConfidentiality,
    );
    this.cancelPropertiesDialog = outputs.cancelPropertiesDialog;
    this.savePropertiesDialog = outputs.savePropertiesDialog;
    this.customLabels = inputs.customLabels ?? [];

    for (const [index, customLabel] of this.customLabels.entries()) {
      this.selectedCustomLabelsOptions[index] = inputs.fileProperties
        .filter((fc: FilePropertyOption) => fc.property_id === customLabel.id)
        .map((fc: FilePropertyOption) =>
          this.getSelectedOption(fc, customLabel as PropertyOption),
        )
        .flat();

      this.selectedCustomLabelsPeople[index] = inputs.fileProperties
        .filter((fc: FilePropertyPeople) => fc.property_id === customLabel.id)
        .map((fc: FilePropertyPeople) => fc.people)
        .flat();

      inputs.suggestedProperties.forEach((suggestedProperty: any) => {
        if (suggestedProperty.property_id === customLabel.id) {
          const labelOption = {
            id: suggestedProperty.property_option_id,
            name:
              suggestedProperty.type === 'PERSON'
                ? ''
                : this.getOptionNameFromId(
                    suggestedProperty.property_option_id,
                    customLabel as PropertyOption,
                  ),
          };
          if (suggestedProperty.type === 'PERSON') {
            this.selectedCustomLabelsPeople[index].push(
              suggestedProperty.person as People,
            );
          } else {
            this.selectedCustomLabelsOptions[index].push(labelOption as any);
          }
        }
      });
    }
    this.peopleLabelFieldAssociatedArray = this.customLabels.map((label) =>
      label.type === 'PERSON' ? label.id : undefined,
    );
    this.hasRight = inputs.hasRight;
  }

  getSelectedOption(fc: FilePropertyOption, customLabel: PropertyOption) {
    return customLabel.options?.filter((option: LabelOption) => {
      return fc.property_option_ids.includes(option.id);
    });
  }

  getOptionNameFromId(optionId: string, customLabel: PropertyOption) {
    return customLabel.options?.find((option: LabelOption) => {
      return option.id === optionId;
    })?.name;
  }

  upperCaseFirstLetter(str: string) {
    if (!str) return '';
    return str?.charAt(0).toUpperCase() + str.slice(1) || '';
  }

  async searchTags(event: AutoCompleteCompleteEvent) {
    this.searchedTags = [];

    // Check if the tag name is not already in the list
    const tagExists = this.tagOptions.filter(
      (tag) => tag?.label.toLowerCase() === event?.query.toLocaleLowerCase(),
    );

    this.searchedTags = this.tagOptions.filter((tag) => {
      return tag?.label.toLowerCase().includes(event.query.toLowerCase());
    });

    if (tagExists.length === 0) {
      // Add the tag to the beginning of the list if it doesn't exist without deleting the other tags
      this.searchedTags.unshift({ id: this.newTagId, label: event.query });
    }
  }

  getSelectedCustomLabels() {
    this.selectedCustomLabelsOptions = this.selectedCustomLabelsOptions.map(
      (subArray) => subArray.filter((item) => item),
    );
    return this.selectedCustomLabelsOptions.flat();
  }

  getSelectedCustomPeople() {
    return this.selectedCustomLabelsPeople
      .map((people, index) => {
        const id = this.peopleLabelFieldAssociatedArray[index];
        if (id)
          return {
            id,
            emails: Array.isArray(people)
              ? people.map((person) => person.email)
              : [(people as People).email],
          };
        return null;
      })
      .filter((item) => item);
  }

  async searchUsers(event: AutoCompleteCompleteEvent) {
    const query: string = event.query;

    this.peopleService
      .list(query)
      .pipe(map((res) => res.items))
      .subscribe((data) => {
        this.usersFromSearch = data;
      });
    return this.usersFromSearch;
  }
}
